import { UnitType } from 'src/Libs/types'
import React from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { buildGraphColorDefinitons } from 'src/Libs/graph-cards/utils'
import { TachoGraphVersion } from 'src/Elements/SystemSettings/SystemSettingsUtils'
import { Breakpoints } from 'src/Configuration/breakpoints'
import { makeStyles } from 'tss-react/mui'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'

const useStyles = makeStyles()(theme => ({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    [`${theme.breakpoints.down('md')} and (orientation: portrait)`]: {
      flexDirection: 'row',
    },
  },
}))

interface UseGraphicBaseCardHook {
  valueOnlyTagLabel: (value: number, index: number, unit: UnitType) => React.ReactNode
  phaseTagLabel: (value: number, index: number, unit: UnitType) => React.ReactNode
  trackerTagLabel: (value: number, index: number, unit: UnitType) => React.ReactNode
  temperatureTagLabel: (value: number, index: number, unit: UnitType) => React.ReactNode
  minMaxTagLabel: (value: number, index: number, unit: UnitType) => React.ReactNode
  getGraphValueBackgroundColor: (index: number, version: TachoGraphVersion, values: number[]) => [string, string]
}

/**
 * Hook provides specific labels used for system settings (e.g. phase, temperature, ...)
 */
const useGraphicBaseCard = (): UseGraphicBaseCardHook => {
  const theme = useTheme()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down(Breakpoints.Desktop))
  const typographyVariant = isMobile ? 'subtitle2' : 'body2'
  const mobileSpacing = isMobile ? 0.6 : 0

  const phaseTagLabel = (value: number, index: number, unit: UnitType): React.ReactNode => {
    return (
      <Box className={classes.contentWrapper}>
        <Typography variant={typographyVariant}>{t('systemSettings.charts.label.labelPhase', { index: index + 1 })}</Typography>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap', pl: mobileSpacing }}>{`${getLocalizedNumber(value)} ${unit}`}</Typography>
      </Box>
    )
  }

  const trackerTagLabel = (value: number, index: number, unit: UnitType): React.ReactNode => {
    return (
      <Box className={classes.contentWrapper}>
        <Typography variant={typographyVariant}>{t('systemSettings.charts.label.labelTracker', { index: index + 1 })}</Typography>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap', pl: mobileSpacing }}>{`${getLocalizedNumber(value)} ${unit}`}</Typography>
      </Box>
    )
  }

  const temperatureTagLabel = (value: number, index: number, unit: UnitType): React.ReactNode => {
    return (
      <Box className={classes.contentWrapper}>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap' }}>
          {t('systemSettings.charts.label.labelTemperature', { index: index + 1 })}
        </Typography>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap', pl: mobileSpacing }}>{`${getLocalizedNumber(value)} ${unit}`}</Typography>
      </Box>
    )
  }

  const valueOnlyTagLabel = (value: number, index: number, unit: UnitType): React.ReactNode => {
    return (
      <Box className={classes.contentWrapper}>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap', pl: mobileSpacing }}>{`${getLocalizedNumber(value)} ${unit}`}</Typography>
      </Box>
    )
  }

  const minMaxTagLabel = (value: number, index: number, unit: UnitType): React.ReactNode => {
    return (
      <Box className={classes.contentWrapper}>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap' }}>
          {index === 0 ? t('systemSettings.charts.label.minimum') : t('systemSettings.charts.label.maximum')}
        </Typography>
        <Typography variant={typographyVariant} sx={{ whiteSpace: 'nowrap', pl: mobileSpacing }}>{`${getLocalizedNumber(value)} ${unit}`}</Typography>
      </Box>
    )
  }

  const getGraphValueBackgroundColor = (index: number, version: TachoGraphVersion, values: number[]): [string, string] => {
    const [graphColorDefinitions, graphColorDefaults] = buildGraphColorDefinitons(theme.palette, values.length ?? 0)
    const graphColors = graphColorDefinitions.get(index) || graphColorDefaults

    if (version === 'light') {
      return [graphColors.light.color, graphColors.light.backgroundColor]
    }

    return [graphColors.default.color, graphColors.default.backgroundColor]
  }

  return { valueOnlyTagLabel, phaseTagLabel, trackerTagLabel, temperatureTagLabel, getGraphValueBackgroundColor, minMaxTagLabel }
}

export default useGraphicBaseCard
